import { getApiBaseUrl } from "../config/runtimeConfig";

const apiBaseUrl = getApiBaseUrl();

async function request<T>(
  path: string,
  options?: RequestInit,
): Promise<T> {
  const response = await fetch(`${apiBaseUrl}${path}`, {
    ...options,
    credentials: "include",
  });

  if (!response.ok) {
    const text = await response.text();
    throw new Error(text || response.statusText);
  }

  const text = await response.text();
  if (!text) {
    return undefined as T;
  }

  return JSON.parse(text) as T;
}

export interface TechnicalDocumentAnalysis {
  id: number;
  budgetDocumentId: number;
  statusCode: string;
  statusName: string;
  extractedAt: string;
  boqLines: PreliminaryBoqLine[];
  isExportReady: boolean;
}

export interface PreliminaryBoqLine {
  id: number;
  description: string;
  quantity: number;
  unit: string;
  measurementMethodCode: string;
  measurementMethodName: string;
  templateLineCode?: string | null;
  sourceReference?: string | null;
  suggestedAreaName?: string | null;
  suggestedChapterName?: string | null;
  isApproved: boolean;
}

export interface ApproveAllConfidentResult {
  approvedCount: number;
}

export function uploadTechnicalDocument(
  budgetId: number,
  file: File,
  category: string,
  descriptionMode: string = "detailed",
  temperature?: number,
): Promise<unknown> {
  const formData = new FormData();
  formData.append("document", file, file.name || "documento");
  formData.append("category", category);
  formData.append("descriptionMode", descriptionMode);
  if (temperature !== undefined) {
    formData.append("temperature", temperature.toString());
  }

  return request<unknown>(
    `/api/technical-documents/budgets/${budgetId}/upload`,
    {
      method: "POST",
      body: formData,
    },
  );
}

export function getTechnicalDocumentAnalysis(
  budgetId: number,
  documentId: number,
): Promise<TechnicalDocumentAnalysis | null> {
  return request<TechnicalDocumentAnalysis | null>(
    `/api/technical-documents/budgets/${budgetId}/documents/${documentId}/analysis`,
  ).catch(() => null);
}

export function approveBoqLine(lineId: number): Promise<void> {
  return request<void>(
    `/api/technical-documents/boq-lines/${lineId}/approve`,
    { method: "POST" },
  );
}

export function approveAllConfidentLines(
  analysisId: number,
): Promise<ApproveAllConfidentResult> {
  return request<ApproveAllConfidentResult>(
    `/api/technical-documents/analyses/${analysisId}/approve-confident`,
    { method: "POST" },
  );
}

export function updateBoqLine(
  lineId: number,
  payload: { description: string; quantity: number; unit: string },
): Promise<void> {
  return request<void>(`/api/technical-documents/boq-lines/${lineId}`, {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
}

export interface ImportAllConfidentResult {
  importedCount: number;
}

export interface ImportAllConfidentByBudgetResult {
  importedCount: number;
  skippedCount: number;
}

export function importAllConfidentLinesByBudget(
  budgetId: number,
): Promise<ImportAllConfidentByBudgetResult> {
  return request<ImportAllConfidentByBudgetResult>(
    `/api/technical-documents/budgets/${budgetId}/import-all-confident`,
    { method: "POST" },
  );
}

export function importBoqLine(
  lineId: number,
  payload: { areaName: string; chapterName: string },
): Promise<void> {
  return request<void>(
    `/api/technical-documents/boq-lines/${lineId}/import`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    },
  );
}

export function importAllConfidentLines(
  analysisId: number,
  payload: { defaultAreaName: string; defaultChapterName: string },
): Promise<ImportAllConfidentResult> {
  return request<ImportAllConfidentResult>(
    `/api/technical-documents/analyses/${analysisId}/import-confident`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    },
  );
}
